import React, { useEffect, useState } from 'react'
import {
  extractPsicolotticoQuestionsAnswersFromJSON,
  extractPsicolotticoResponseDataFromJSON
} from '../utils/extract-data-from-json'
import Select from '../components/Input/select'
import { fetchQuestsForSelectedTest } from '../utils/mock-utils'
import ExpandableContainer from '../components/ui/expandable-container'
import { PsicolotticoResponseJSON } from '../types/output-data'
import { getPiscolotticoResponse } from '../services/backend-services'
import { SIGNAL_EVENT, TEST_PSICOLOTTICO } from '../types/enums'
import SignalsService from '../services/communication/signals.service'
import PostMessageService, { PostMessageType } from '../services/communication/post-message.service'
import Button from '../components/ui/button'
import ErrorMessage from '../components/ui/error-message'
import Resizer from '../components/ui/resizer'
import Number from '../components/ui/number'

const TestPsicolottico = () => {
  const [showSelects, setShowSelects] = useState(false)
  const [showNumbers, setShowNumbers] = useState(false)
  const [canChangeAnswers, setCanChangeAnswers] = useState(true)
  const [selectedTest, setSelectedTest] = useState(0)
  const [showError, setShowError] = useState(false)

  // le domande e le risposte che provengono dalla chiamata di rete e che saranno mostrate
  const [questionsAndResponseToShow, setQuestionsAndResponseToShow] = useState<
    Record<string, string[]>
  >({})
  // mappa che associa la risposta selezionata ad ogni domanda
  const [question2response, setQuestion2response] = useState<Map<number, string>>(new Map())
  const [response, setResponse] = useState<PsicolotticoResponseJSON>({
    description: '',
    profile: '',
    randomNumbers: ['']
  })

  const allTests = [
    TEST_PSICOLOTTICO.LupoAgnello,
    TEST_PSICOLOTTICO.GodersiVita,
    TEST_PSICOLOTTICO.PaperoneGastone
  ]
  const psicolotticoTestId = 'psicolottico'
  const isDefaultNone = true
  const transitionStyle = 'transition-vertical-spacing duration-700 ease-out'

  const handleButtonClick = () => {
    // se non erano visibili le select ora le voglio mostrare
    if (!showSelects) {
      setShowSelects(true)
    }

    // se c'erano le select e il risultato, allora nascondo solo il risultato
    if (showSelects && showNumbers) {
      setShowNumbers(false)
      // Qui non viene resettata la risposta altrimenti, durante l'animazione si vedrebbe che si svuota il contenuto nel container con i numeri
    }

    // necessario perché si resettano le risposte e quindi i valori nelle select che tornano al valore di default
    setQuestion2response(new Map())
    // se ho cliccato sul bottone posso far rispondere alle domande
    setCanChangeAnswers(true)
    // risetto le domande e le risposte possibili per il test appena selezionato
    // TODO: fare una lista di domande e risposta che vengono caricate tutte insieme al primo load della pagina? o caricare ogni volta le domande e le risposte per lo step corrente?
    setQuestionsAndResponseToShow(getQuestionAndResponse())
  }

  const handleTestSelectOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTest(allTests.findIndex((item) => item === e.target.value))
  }

  // aggiorno la mappa che associa le risposte possibili alle domande
  const handleQuestionSelectOnChange = (e: React.ChangeEvent<HTMLSelectElement>, index: number) => {
    setQuestion2response((prevMap) => new Map(prevMap).set(index, e.target.value))
  }

  const areAllQuestionAnswered = () => {
    const questionToBeAnswered = Object.entries(questionsAndResponseToShow).length
    return (
      question2response.size > 0 &&
      questionToBeAnswered > 0 &&
      questionToBeAnswered === question2response.size
    )
  }

  // le risposte e le domande sono fisse per ogni test
  const getQuestionAndResponse = () => {
    const questsJson = fetchQuestsForSelectedTest(selectedTest)
    return extractPsicolotticoQuestionsAnswersFromJSON(questsJson as any)
  }

  // ogni volta che rispondo e quindi la mappa seguente è modificata, controllo che tutte le domande abbiano risposta
  // se sì, devo procedere con il retrieve del numero, impossibilitare il cambio risposta, ma è ancora possibile cambiare il test
  useEffect(() => {
    const fetchPsicolotticoResponse = async () => {
      try {
        SignalsService.fire(SIGNAL_EVENT.showLoader, 'flex')
        const responseJson = await getPiscolotticoResponse(
          allTests[selectedTest],
          Object.keys(questionsAndResponseToShow),
          Array.from(question2response.values())
        )
        const response = extractPsicolotticoResponseDataFromJSON(responseJson)
        setResponse(response)
      } catch (e) {
        if (e instanceof Error) {
          console.error('Test psicolottico:', e.message)
          setShowError(true)
        }
      } finally {
        SignalsService.fire(SIGNAL_EVENT.showLoader, 'hidden')
      }
    }

    setShowError(false)

    if (areAllQuestionAnswered()) {
      fetchPsicolotticoResponse()
    }
  }, [question2response])

  useEffect(() => {
    if (
      response &&
      response.profile &&
      response.randomNumbers &&
      response.randomNumbers.length > 0 &&
      response.description
    ) {
      setCanChangeAnswers(false)
      setShowNumbers(true)
    } else {
      setCanChangeAnswers(true)
      setShowNumbers(false)
    }
  }, [response])

  // non è possibile mettere solo la response come dipendenza perché quando si clicca il bottone, essa non viene modificata e non verrebbe triggerato questo useEffect
  // altrimenti si potrebbe resettare la response all'animationEnd
  useEffect(() => {
    if (showNumbers && response.randomNumbers && response.randomNumbers.length > 0) {
      PostMessageService.send({
        type: PostMessageType.PLAY_NUMBERS,
        numbers: response.randomNumbers
      })
    } else {
      PostMessageService.send({
        type: PostMessageType.RESET_NUMBERS
      })
    }
  }, [showNumbers, response])

  // quando viene selezionato il test, resetto lo stato e recupero i dati associati a quel test
  useEffect(() => {
    setShowError(false)
    setShowSelects(false)
    setShowNumbers(false)
    // se avevo un test già selezionato devo resettare tutto quello calcolato in precedenza e
    // nascondere le altre select, dipendenti dal test selezionato
    setCanChangeAnswers(true)
    setResponse({
      description: '',
      profile: '',
      randomNumbers: ['']
    })
    setQuestion2response(new Map())
  }, [selectedTest])

  return (
    <Resizer idResizer={psicolotticoTestId}>
      <div className={'w-full h-full text-base bg-lightOrangeColor'}>
        <form>
          <div className={`grid grid-cols-1 desktop:grid-cols-2 py-5`}>
            <div className={'px-3.7 w-full'}>
              <div className={'px-3.7'}>
                <p className={'pb-2.75'}>Scegli uno dei nostri test:</p>
              </div>
              <div className={'mb-3.7 desktop:max-w-[450px] w-full'}>
                <Select
                  options={allTests}
                  idSelect={'scegli_test'}
                  onChange={handleTestSelectOnChange}
                />
              </div>
            </div>
            <div className={'px-3.7 w-full relative top-2.5 flex items-center h-20'}>
              <Button
                idButton={psicolotticoTestId}
                buttonText={'scegli il tuo test'}
                className={'desktop:w-2/3 max-h-[54.8px] desktop:max-w-[380px]'}
                handleClick={handleButtonClick}
              />
            </div>
          </div>
          <div
            className={`${transitionStyle} overflow-hidden desktop:col-span-2 ${showSelects ? 'h-32' : 'h-0'}`}
          />
          <ExpandableContainer
            id={psicolotticoTestId + '-questions'}
            expandableCondition={showSelects}
            className={`${transitionStyle} overflow-hidden  ${showSelects ? 'py-5' : ''}`}
            tot_padding_y={40}>
            <div className={'grid grid-cols-1 desktop:grid-cols-2'}>
              {Object.entries(questionsAndResponseToShow)?.map(([domanda, risposte], index) => (
                <div className={'w-full mb-5 flex items-end'} key={index}>
                  <div className={'px-3.7 w-full'}>
                    <div className={'px-3.7'}>
                      <p className={'pb-2.75'}>{domanda}</p>
                    </div>
                    <div className={'mb-3.7 w-full'}>
                      <Select
                        options={risposte}
                        idSelect={'domanda' + index}
                        isDefaultNone={isDefaultNone && !question2response.get(index)}
                        onChange={(e) => handleQuestionSelectOnChange(e, index)}
                        isDisabled={!canChangeAnswers}
                        value={question2response.get(index) || ''}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ExpandableContainer>
          <ExpandableContainer
            id={psicolotticoTestId + '-response'}
            expandableCondition={showSelects && showNumbers}
            className={`${transitionStyle} overflow-hidden ${showSelects && showNumbers ? 'py-5' : ''}`}
            tot_padding_y={40}>
            <div className={'px-3.7'}>
              <div className={'py-5'}>
                <h2>{response.profile}</h2>
                <p className={'text-base font-normal mb-2.75'}>{response?.description}</p>
              </div>
              <div className={'py-3.7 w-full flex mobile:flex-col items-center justify-center'}>
                <div className={'px-3.7'}>
                  <h2>I Tuoi numeri:</h2>
                </div>
                <div
                  className={'px-3.7 flex justify-center mobile:w-full mobile:justify-between'}
                  id={psicolotticoTestId + '-numbers-container'}>
                  {response?.randomNumbers?.map((numero, idx) => (
                    <Number
                      key={`${psicolotticoTestId}-${numero}-${idx}`}
                      idNumber={`${psicolotticoTestId}-${idx}`}
                      numberTxt={numero}
                      customClassName={
                        'mx-1.5 mobile:mx-3.7 h-[50px] desktop:w-[50px] tablet:w-[40px] mobile:flex-grow'
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          </ExpandableContainer>
        </form>
      </div>
      <ErrorMessage idError={psicolotticoTestId} isVisible={showError} />
    </Resizer>
  )
}

export default TestPsicolottico
